/** Shared classes are what keep the Budget and Spend grids reading as one thing. */

// Separate borders: a collapsed table drops the sticky column's as soon as the grid scrolls.
const borders = 'border-b border-r border-border';
// Weight is set per cell: two utilities for one property are settled by emit order, lightest first.
const cellText = 'text-sm text-foreground max-md:text-xs';
/** Mixed flat rather than layered: a sticky cell has to be opaque or the sprints scroll through it. */
const stuckTint = 'bg-[color-mix(in_oklab,var(--muted)_40%,var(--card))]';
const stuckBg = `sticky left-0 z-10 ${stuckTint}`;
/** Exported because the scroll rail is the row under it and has to match. */
export const gridStrongTint = 'bg-[color-mix(in_oklab,var(--muted)_60%,var(--card))]';
const stuckBgStrong = `sticky left-0 z-10 ${gridStrongTint}`;

export const gridHeadTint = 'bg-muted/40';

// Read by the statement of work, the one money table here that is a table rather than a grid of rows.
const gridCell = `h-10 ${borders}`;
export const gridPadded = `${gridCell} px-3 max-md:px-2`;
export const gridHeadText = `${gridPadded} ${cellText} font-medium`;

/**
 * The member column's width, which is also how far in the scroll rail starts: it does not scroll. A
 * variable, narrower on a phone (globals.css), where 18rem of names left no room for a single sprint.
 */
export const hoursNameWidth = 'var(--hours-name-width)';

/** Narrower than a sprint: a year's total is one figure, not a sprint's dates. */
const hoursTotalWidth = 'var(--hours-total-width)';

export const hoursColumns = (sprints: number, withTotal = false): string => {
  // `repeat(0, …)` is not valid CSS, and the whole template goes with it.
  const template = sprints > 0 ? `${hoursNameWidth} repeat(${sprints}, var(--hours-column-width))` : hoursNameWidth;
  return withTotal ? `${template} ${hoursTotalWidth}` : template;
};
/** Rows of CSS grid, not tables: a table row group ignores `overflow`, so a fold would squash rows. */
export const hoursRow = 'grid';

/** No sideways scroll here, so the columns share what is left after the name. */
// First read by slice 8 (#540), whose out-of-pocket table is the one that does not scroll sideways.
// A phone is the exception: the names narrow and the quarters keep a readable floor, so it scrolls there.
export const costColumns = 'var(--cost-name-width) repeat(5, minmax(var(--cost-column-min), 1fr))';

// Fixed height, so a chapter's people are exactly `rows × --row-h` and the fold never measures.
const hoursBase = `flex h-[var(--row-h,2.5rem)] items-center ${borders} last:border-r-0`;
/** A figure. */
export const hoursCell = `${hoursBase} justify-end px-3 max-md:px-2 ${cellText}`;
/** A cell an input fills edge to edge, which carries its own padding. */
export const hoursCellFlush = `${hoursBase} justify-end ${cellText}`;
export const hoursHeadCell = `${hoursCell} font-medium`;
/** The member column, which stays put while the year scrolls past it. */
export const hoursNameCell = `${hoursBase} justify-start px-3 max-md:px-2 ${stuckBg} ${cellText}`;
/** The same, for a cell an input or a button fills: it pads itself. */
export const hoursNameFlush = `${hoursBase} justify-start ${stuckBg} ${cellText}`;

const endCell = `${hoursBase} justify-end px-2 ${cellText}`;
/** The total column, last in the scroll: what the year came to is read after the year. */
export const hoursEndCell = `${endCell} ${stuckTint}`;
export const hoursEndStrong = `${endCell} ${gridStrongTint}`;

/** And for the total row, which is tinted harder than the rest. */
export const hoursTotalName = `${hoursBase} justify-start px-3 max-md:px-2 ${stuckBgStrong} ${cellText}`;

/** `overflow-clip`, not `hidden`: hidden is a scroll container, and the name column sticks to one. */
export const hoursFoldBox =
  'overflow-clip transition-[height,visibility] duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)] motion-reduce:transition-none';
export const hoursFoldOpen = 'visible h-[calc(var(--rows)*var(--row-h,2.5rem))] [transition-delay:0ms,0ms]';
export const hoursFoldShut = 'invisible h-0 [transition-delay:0ms,400ms]';

/** A forecast's columns: green where the sprint is closed and spent, amber where it is still projected. */
export const actualTint = 'bg-[color-mix(in_srgb,#1f9d55_6%,var(--card))]';
export const actualTintStrong = 'bg-[color-mix(in_srgb,#1f9d55_11%,var(--card))]';
export const projectedTint = 'bg-[color-mix(in_srgb,#d9a400_6%,var(--card))]';
export const projectedTintStrong = 'bg-[color-mix(in_srgb,#d9a400_11%,var(--card))]';

/** An inset shadow, not a background: a background would replace a forecast column's tint. */
export const hoverDarken = 'hover:shadow-[inset_0_0_0_9999px_rgba(0,0,0,0.06)]';
export const hoverDarkenRow = 'group-hover/row:shadow-[inset_0_0_0_9999px_rgba(0,0,0,0.03)]';
